import { warn } from "@/lib/log";
// Session-only storage: household data lives in this browser tab and is cleared when the tab or browser closes.
// Only non-personal preferences (cookie choice, language, nation) and the two-week checklist ticks persist between visits.
const PERSIST = new Set(["ukready_consent", "ukready_lang", "ukready_nation", "ukready_two_week_v1"]);
const box = (k) => (PERSIST.has(k) ? localStorage : sessionStorage);

const read = (k, fallback) => {
  try { const v = box(k).getItem(k); return v ? JSON.parse(v) : fallback; }
  catch (e) { warn(`Could not read ${k}`, e); return fallback; }
};
// Write failures (private mode, quota) are logged but must not crash the app.
const write = (k, v) => { try { box(k).setItem(k, JSON.stringify(v)); } catch (e) { warn(`Could not save ${k}`, e); } };
const remove = (k) => { try { box(k).removeItem(k); } catch (e) { warn(`Could not clear ${k}`, e); } };

// One-time move of data saved by earlier versions out of long-lived storage into this session.
export function migrateToSession() {
  try {
    Object.keys(localStorage).filter((k) => k.startsWith("ukready_") && !PERSIST.has(k)).forEach((k) => {
      if (sessionStorage.getItem(k) === null) sessionStorage.setItem(k, localStorage.getItem(k));
      localStorage.removeItem(k);
    });
  } catch (e) { warn("Storage migration skipped", e); }
}

export const store = {
  getPlan: () => read("ukready_plan", null),
  savePlan: (p) => write("ukready_plan", { ...p, _saved: new Date().toISOString() }),
  getKit: () => read("ukready_kit", {}),
  saveKit: (k) => write("ukready_kit", k),
  getArea: () => read("ukready_area", null),
  saveArea: (a) => write("ukready_area", a),
  clearArea: () => remove("ukready_area"),
  getFive: () => read("ukready_five", {}),
  saveFive: (f) => write("ukready_five", f),
  getReadiness: () => read("ukready_readiness", null),
  saveReadiness: (r) => write("ukready_readiness", r),
  getNation: () => read("ukready_nation", "England"),
  saveNation: (n) => write("ukready_nation", n),
  getLang: () => read("ukready_lang", "en"),
  saveLang: (l) => write("ukready_lang", l),
  getSavedArea: () => read("ukready_saved_area", null),
  saveSavedArea: (a) => write("ukready_saved_area", a),
  clearSavedArea: () => remove("ukready_saved_area"),
  getWatch: () => read("ukready_watch", null),
  saveWatch: (w) => write("ukready_watch", w),
  clearWatch: () => remove("ukready_watch"),
  getWatchSeen: () => read("ukready_watch_seen", []),
  saveWatchSeen: (a) => write("ukready_watch_seen", a),
  getConsent: () => read("ukready_consent", null),
  setConsent: (v) => write("ukready_consent", v),
  getOffline: () => read("ukready_offline_guides", []),
  saveOffline: (list) => write("ukready_offline_guides", list),
  getWallet: () => read("ukready_wallet", null),
  saveWallet: (w) => write("ukready_wallet", w),
};

export const postcodeToNation = (pc) => {
  const p = (pc || "").trim().toUpperCase();
  if (p.startsWith("BT")) return "Northern Ireland";
  const scot = ["AB", "DD", "DG", "EH", "FK", "HS", "IV", "KA", "KW", "KY", "ML", "PA", "PH", "TD", "ZE", "G"];
  if (scot.some((s) => p.startsWith(s))) return "Scotland";
  const wales = ["CF", "LL", "NP", "SA", "LD", "SY"];
  if (wales.some((s) => p.startsWith(s))) return "Wales";
  return "England";
};
